import { useEffect, useState } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { Star, Camera, AtSign, Eye, Zap, Save } from 'lucide-react'
import { api, type FormConfig, type ProjectWithStats } from '../lib/api'
import { Spinner } from '../components/ui'


export function FormBuilder() {
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const initialProject = searchParams.get('project') ?? ''

  const [projects, setProjects] = useState<ProjectWithStats[]>([])
  const [projectId, setProjectId] = useState(initialProject)
  const [config, setConfig] = useState<FormConfig | null>(null)
  const [dirty, setDirty] = useState(false)
  const [saving, setSaving] = useState(false)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    api.projects.list().then(({ projects: p }) => {
      setProjects(p)
      if (!projectId) setLoading(false) // no URL param — wait for user to pick
    }).catch(err => {
      setLoading(false)
      if (err.message?.toLowerCase().includes('unauthorized') || err.message?.includes('401')) {
        navigate('/login', { replace: true })
      }
    })
  }, [])

  useEffect(() => {
    if (!projectId) return
    setLoading(true)
    api.formConfig.get(projectId)
      .then(c => { setConfig(c); setDirty(false) })
      .catch(err => {
        if (err.message?.toLowerCase().includes('unauthorized') || err.message?.includes('401')) {
          navigate('/login', { replace: true })
        }
      })
      .finally(() => setLoading(false))
  }, [projectId])

  function update<K extends keyof FormConfig>(key: K, value: FormConfig[K]) {
    setConfig(prev => prev ? { ...prev, [key]: value } : prev)
    setDirty(true)
  }

  async function save() {
    if (!config || !projectId) return
    setSaving(true)
    try {
      const updated = await api.formConfig.update(projectId, config)
      // Merge response with local config to preserve any fields the API might not return
      setConfig(prev => prev ? { ...prev, ...updated } : updated)
      setDirty(false)
    } catch (err: any) {
      alert(`Save failed: ${err.message ?? 'Unknown error'}`)
    } finally { setSaving(false) }
  }

  function discard() {
    if (!projectId) return
    api.formConfig.get(projectId).then(c => { setConfig(c); setDirty(false) })
  }

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 'calc(100vh - 64px)' }}>
      <Spinner />
    </div>
  )

  return (
    <div style={{ display: 'flex', gap: 24, padding: '24px 32px', height: 'calc(100vh - 64px)', boxSizing: 'border-box', overflow: 'hidden' }}>

      {/* Left: Configuration Panel */}
      <section style={{ width: '340px', flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 16, overflowY: 'auto', paddingRight: 4 }}>

        {/* Header */}
        <div>
          <h3 style={{ fontSize: 13, fontWeight: 600, color: '#777587', fontFamily: 'Inter, sans-serif', margin: '0 0 10px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Form Builder
          </h3>
          <div style={{
            background: 'rgba(53,37,205,0.06)', border: '1px solid rgba(53,37,205,0.15)',
            borderRadius: 12, padding: '12px 14px',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
          }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 11, color: '#777587', fontWeight: 500, marginBottom: 2, fontFamily: 'Inter, sans-serif' }}>
                {projectId ? 'Editing form for' : 'Select a project'}
              </div>
              <div style={{ fontSize: 15, fontWeight: 700, color: '#3525cd', fontFamily: 'Manrope, sans-serif', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
                {projects.find(p => p.id === projectId)?.name ?? '—'}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0 }}>
              <select
                value={projectId}
                onChange={e => { setProjectId(e.target.value); setSearchParams({ project: e.target.value }) }}
                style={{
                  background: '#ffffff', border: '1px solid rgba(53,37,205,0.2)', borderRadius: 8,
                  padding: '5px 10px', fontSize: 12, color: '#3525cd', fontWeight: 600,
                  fontFamily: 'Inter, sans-serif', outline: 'none', cursor: 'pointer',
                }}
              >
                <option value="" disabled>Choose project…</option>
                {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
              {dirty && (
                <button
                  onClick={save}
                  disabled={saving}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 4,
                    fontSize: 11, fontWeight: 700, color: '#3525cd',
                    background: 'rgba(53,37,205,0.1)', border: 'none',
                    borderRadius: 99, padding: '5px 12px', cursor: 'pointer',
                    fontFamily: 'Inter, sans-serif',
                  }}
                >
                  <Save size={11} />{saving ? 'Saving…' : 'Save'}
                </button>
              )}
            </div>
          </div>
        </div>

        {!config ? (
          <div style={{ color: '#777587', fontSize: 13 }}>Select a project to configure its form.</div>
        ) : (
          <>
            {/* Theme & Identity */}
            <ConfigSection title="Theme & Identity">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <ColorCard label="Primary Color" value={config.primary_color} onChange={v => update('primary_color', v)} />
                <ColorCard label="Background" value={config.background_color} onChange={v => update('background_color', v)} />
                <ColorCard label="Title Color" value={config.title_color} onChange={v => update('title_color', v)} />
                <ColorCard label="Button Text" value={config.button_text_color} onChange={v => update('button_text_color', v)} />
                <ColorCard label="Star Active" value={config.star_color_active} onChange={v => update('star_color_active', v)} />
                <ColorCard label="Star Inactive" value={config.star_color_inactive} onChange={v => update('star_color_inactive', v)} />
                <ColorCard label="Input Bg" value={config.input_background_color} onChange={v => update('input_background_color', v)} />
                <ColorCard label="Input Border" value={config.input_border_color} onChange={v => update('input_border_color', v)} />
                <ColorCard label="Placeholder" value={config.placeholder_color} onChange={v => update('placeholder_color', v)} />
                <ColorCard label="Success Color" value={config.success_color} onChange={v => update('success_color', v)} />
                <ColorCard label="Tagline Color" value={config.tagline_color || '#64748b'} onChange={v => update('tagline_color', v)} />
              </div>
              <div style={{ marginTop: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <label style={{ fontSize: 12, fontWeight: 500, color: '#464555' }}>Corner Radius</label>
                  <span style={{ fontSize: 12, fontWeight: 700, color: '#3525cd' }}>{config.border_radius}px</span>
                </div>
                <input
                  type="range" min={0} max={24}
                  value={config.border_radius}
                  onChange={e => update('border_radius', Number(e.target.value))}
                  style={{ width: '100%', accentColor: '#3525cd' }}
                />
              </div>
            </ConfigSection>

            {/* Active Modules */}
            <ConfigSection title="Active Modules">
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {([
                  { key: 'show_rating' as const, label: 'Rating Widget', Icon: Star },
                  { key: 'show_screenshot' as const, label: 'Screenshot Upload', Icon: Camera },
                  { key: 'show_email' as const, label: 'Email Field', Icon: AtSign },
                ]).map(({ key, label, Icon }) => (
                  <div key={key} style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '10px 14px', background: '#ffffff', borderRadius: 10,
                    border: '1px solid #eceef0', boxShadow: '0 1px 2px rgba(25,28,30,0.04)',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <Icon size={16} color="#3525cd" />
                      <span style={{ fontSize: 13, fontWeight: 500, color: '#191c1e' }}>{label}</span>
                    </div>
                    <Toggle on={config[key]} onChange={v => update(key, v)} />
                  </div>
                ))}
              </div>
            </ConfigSection>

            {/* Interface Text */}
            <ConfigSection title="Interface Text">
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {([
                  { key: 'form_title' as const, label: 'Header Label', placeholder: 'How was your experience?' },
                  { key: 'tagline' as const, label: 'Tagline', placeholder: 'Your feedback helps us grow.' },
                  { key: 'placeholder_text' as const, label: 'Placeholder Text', placeholder: 'Tell us...' },
                  { key: 'button_label' as const, label: 'Button Label', placeholder: 'Send Feedback' },
                  { key: 'success_message' as const, label: 'Success Message', placeholder: 'Thanks!' },
                  { key: 'email_placeholder' as const, label: 'Email Placeholder', placeholder: 'Email (optional)' },
                  { key: 'screenshot_label' as const, label: 'Screenshot Button Text', placeholder: 'ATTACH SCREENSHOT' },
                  { key: 'screenshot_icon' as const, label: 'Screenshot Icon (emoji)', placeholder: '📷' },
                ]).map(({ key, label, placeholder }) => (
                  <div key={key} style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                    <label style={{ fontSize: 12, fontWeight: 500, color: '#464555' }}>{label}</label>
                    <input
                      value={(config[key] as string) ?? ''}
                      onChange={e => update(key, e.target.value)}
                      placeholder={placeholder}
                      style={{
                        background: '#ffffff', border: 'none', borderRadius: 8,
                        padding: '9px 12px', fontSize: 13, color: '#191c1e',
                        fontFamily: 'Inter, sans-serif', outline: 'none',
                        boxShadow: '0 1px 2px rgba(25,28,30,0.06)',
                      }}
                    />
                  </div>
                ))}
              </div>
            </ConfigSection>

            {dirty && (
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  onClick={discard}
                  style={{
                    flex: 1, padding: '9px 0', background: '#eceef0', border: 'none',
                    borderRadius: 8, fontSize: 13, fontWeight: 600, color: '#464555',
                    cursor: 'pointer', fontFamily: 'Inter, sans-serif',
                  }}
                >
                  Discard
                </button>
                <button
                  onClick={save}
                  disabled={saving}
                  style={{
                    flex: 2, padding: '9px 0', background: '#3525cd', border: 'none',
                    borderRadius: 8, fontSize: 13, fontWeight: 600, color: '#ffffff',
                    cursor: 'pointer', fontFamily: 'Inter, sans-serif',
                    opacity: saving ? 0.7 : 1,
                  }}
                >
                  {saving ? 'Saving…' : 'Save Changes'}
                </button>
              </div>
            )}
          </>
        )}
      </section>

      {/* Right: Live Preview */}
      <section style={{
        flex: 1, background: '#eceef0', borderRadius: 24,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        position: 'relative', overflow: 'hidden', padding: 48,
      }}>
        {/* Live Preview Badge */}
        <div style={{ position: 'absolute', top: 32, left: 32 }}>
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            background: 'rgba(53,37,205,0.1)', color: '#3525cd',
            padding: '8px 16px', borderRadius: 99, fontSize: 14, fontWeight: 700,
          }}>
            <Eye size={14} /> Live Preview
          </span>
        </div>

        {!config ? (
          <div style={{ textAlign: 'center', color: '#777587' }}>
            <div style={{ fontSize: 36, marginBottom: 12 }}>📱</div>
            <div style={{ fontSize: 14, fontWeight: 500 }}>Select a project to preview your form</div>
          </div>
        ) : (
          /* Phone Mockup — exact match to HTML: w-[320px] h-[640px] bg-slate-900 rounded-[3rem] p-3 border-[8px] border-slate-800 */
          <div style={{
            position: 'relative',
            width: 320, height: 640,
            background: '#0f172a',
            borderRadius: 48,
            padding: 12,
            border: '8px solid #1e293b',
            boxShadow: '0 25px 50px rgba(0,0,0,0.4)',
          }}>
            {/* Notch — absolute top-0 left-1/2 -translate-x-1/2 w-32 h-6 bg-slate-800 rounded-b-2xl */}
            <div style={{
              position: 'absolute', top: 0, left: '50%', transform: 'translateX(-50%)',
              width: 128, height: 24,
              background: '#1e293b',
              borderRadius: '0 0 16px 16px',
              zIndex: 10,
            }} />

            {/* Screen — bg-slate-100 rounded-[2.2rem] */}
            <div style={{
              width: '100%', height: '100%',
              background: '#f1f5f9',
              borderRadius: 35,
              overflow: 'hidden',
              position: 'relative',
            }}>
              {/* App Content Simulation — p-6 pt-12 opacity-40 grayscale */}
              <div style={{
                padding: '48px 24px 24px',
                opacity: 0.4,
                filter: 'grayscale(1)',
                pointerEvents: 'none',
                display: 'flex', flexDirection: 'column', gap: 16,
              }}>
                {/* h-8 w-1/2 bg-slate-300 rounded */}
                <div style={{ height: 32, width: '50%', background: '#cbd5e1', borderRadius: 4 }} />
                {/* h-40 w-full bg-slate-300 rounded-xl */}
                <div style={{ height: 160, width: '100%', background: '#cbd5e1', borderRadius: 12 }} />
                {/* two text lines */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <div style={{ height: 16, width: '100%', background: '#cbd5e1', borderRadius: 4 }} />
                  <div style={{ height: 16, width: '83%', background: '#cbd5e1', borderRadius: 4 }} />
                </div>
              </div>

              {/* Feedback Modal Overlay — absolute inset-0 bg-black/30 backdrop-blur-sm flex items-end */}
              <div style={{
                position: 'absolute', inset: 0,
                background: 'rgba(0,0,0,0.3)',
                backdropFilter: 'blur(4px)',
                WebkitBackdropFilter: 'blur(4px)',
                display: 'flex', alignItems: 'flex-end',
              }}>
                {/* Bottom Sheet — bg-white rounded-t-3xl p-6 pb-10 shadow-2xl translate-y-2 */}
                <div style={{
                  width: '100%',
                  background: '#ffffff',
                  borderRadius: '24px 24px 0 0',
                  padding: '24px 24px 40px',
                  boxShadow: '0 -25px 50px rgba(0,0,0,0.25)',
                  transform: 'translateY(8px)',
                  display: 'flex', flexDirection: 'column', gap: 20,
                }}>
                  {/* Drag handle — w-12 h-1 bg-slate-200 rounded-full mx-auto -mt-2 */}
                  <div style={{
                    width: 48, height: 4,
                    background: '#e2e8f0',
                    borderRadius: 99,
                    margin: '-8px auto 0',
                  }} />

                  {/* Title */}
                  <div style={{ textAlign: 'center' }}>
                    <h5 style={{
                      fontSize: 15, fontWeight: 700, color: '#0f172a',
                      margin: '0 0 4px', fontFamily: 'Manrope, sans-serif',
                    }}>
                      {config.form_title || 'Send us your thoughts'}
                    </h5>
                    <p style={{ fontSize: 12, color: config.tagline_color || '#64748b', margin: 0 }}>
                      {config.tagline || 'Your feedback helps us grow.'}
                    </p>
                  </div>

                  {/* Rating — flex justify-center gap-2, text-3xl */}
                  {config.show_rating && (
                    <div style={{ display: 'flex', justifyContent: 'center', gap: 8 }}>
                      {[1, 2, 3, 4, 5].map(s => (
                        <span key={s} style={{
                          fontSize: 30,
                          color: s <= 4 ? config.star_color_active : (config.star_color_inactive || '#e2e8f0'),
                          lineHeight: 1,
                        }}>★</span>
                      ))}
                    </div>
                  )}

                  {/* Message text area + fields */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {/* Message box */}
                    <div style={{
                      background: '#f8fafc',
                      border: '1px solid #f1f5f9',
                      borderRadius: config.border_radius,
                      padding: 16,
                      minHeight: 80,
                    }}>
                      <p style={{ fontSize: 12, color: '#94a3b8', margin: 0, lineHeight: 1.6 }}>
                        {config.placeholder_text || 'Tell us what\'s on your mind...'}
                      </p>
                    </div>

                    {/* Email — above screenshot */}
                    {config.show_email && (
                      <div style={{
                        background: '#f8fafc', border: '1px solid #f1f5f9',
                        borderRadius: config.border_radius, padding: '12px 16px',
                      }}>
                        <p style={{ fontSize: 12, color: '#94a3b8', margin: 0 }}>
                          {config.email_placeholder || 'Email (optional)'}
                        </p>
                      </div>
                    )}

                    {/* Screenshot button — below email */}
                    {config.show_screenshot && (
                      <button style={{
                        width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        gap: 8, padding: '12px 0',
                        background: '#f8fafc', border: '1px solid #e2e8f0',
                        borderRadius: config.border_radius, color: '#475569', cursor: 'pointer',
                        fontFamily: 'Inter, sans-serif',
                      }}>
                        <span style={{ fontSize: 20 }}>{config.screenshot_icon || '📷'}</span>
                        <span style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                          {config.screenshot_label || 'Attach Screenshot'}
                        </span>
                      </button>
                    )}
                  </div>

                  {/* Submit button */}
                  <div style={{
                    width: '100%', padding: '16px 0', textAlign: 'center',
                    background: config.primary_color,
                    color: config.button_text_color,
                    fontWeight: 700, fontSize: 14,
                    borderRadius: config.border_radius,
                    boxShadow: `0 10px 25px ${config.primary_color}33`,
                  }}>
                    {config.button_label || 'Submit Feedback'}
                  </div>

                  {/* Success message */}
                  <div style={{ textAlign: 'center', marginTop: -4 }}>
                    <p style={{
                      fontSize: 11, fontWeight: 500, color: '#059669',
                      display: 'inline-flex', alignItems: 'center', gap: 6,
                      margin: 0,
                    }}>
                      <span style={{ fontSize: 14 }}>✓</span>
                      {config.success_message || 'Success! Your feedback has been sent.'}
                    </p>
                  </div>

                  {/* Powered by */}
                  <p style={{ fontSize: 10, textAlign: 'center', color: '#94a3b8', margin: 0 }}>
                    Powered by FeedbackKit.io
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Dynamic Sync tooltip — glass-panel bottom-8 right-8 max-w-[240px] rounded-2xl */}
        <div style={{
          position: 'absolute', bottom: 32, right: 32,
          maxWidth: 240,
          background: 'rgba(255,255,255,0.8)',
          backdropFilter: 'blur(24px)', WebkitBackdropFilter: 'blur(24px)',
          padding: 16, borderRadius: 16,
          boxShadow: '0 2px 12px rgba(25,28,30,0.08)',
          border: '1px solid rgba(255,255,255,0.4)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
            <div style={{ padding: 6, background: 'rgba(53,37,205,0.1)', borderRadius: 8, display: 'flex' }}>
              <Zap size={12} color="#3525cd" />
            </div>
            <span style={{ fontSize: 12, fontWeight: 700, color: '#1e293b' }}>Dynamic Sync</span>
          </div>
          <p style={{ fontSize: 11, color: '#64748b', margin: 0, lineHeight: 1.5 }}>
            The live preview renders using our native SDK components to ensure what you see is exactly what your users will experience.
          </p>
        </div>
      </section>
    </div>
  )
}

function ConfigSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ background: '#f2f4f6', borderRadius: 12, padding: '16px 20px' }}>
      <h4 style={{
        fontSize: 10, fontWeight: 800, textTransform: 'uppercase',
        letterSpacing: '0.08em', color: '#777587', margin: '0 0 14px',
      }}>
        {title}
      </h4>
      {children}
    </div>
  )
}

function ColorCard({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
      <label style={{ fontSize: 11, fontWeight: 500, color: '#464555' }}>{label}</label>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8, padding: '7px 10px',
        background: '#ffffff', borderRadius: 8,
        boxShadow: '0 1px 2px rgba(25,28,30,0.06)', border: '1px solid #eceef0',
        cursor: 'pointer', position: 'relative',
      }}>
        <div style={{ width: 18, height: 18, borderRadius: 4, background: value, flexShrink: 0, border: '1px solid rgba(25,28,30,0.1)' }} />
        <span style={{ fontSize: 11, fontFamily: 'monospace', color: '#777587', flex: 1 }}>{value}</span>
        <input
          type="color"
          value={value}
          onChange={e => onChange(e.target.value)}
          style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer', width: '100%', height: '100%', border: 'none', padding: 0 }}
        />
      </div>
    </div>
  )
}

function Toggle({ on, onChange }: { on: boolean; onChange: (v: boolean) => void }) {
  return (
    <button
      onClick={() => onChange(!on)}
      style={{
        width: 36, height: 20, borderRadius: 10, border: 'none', cursor: 'pointer',
        background: on ? '#3525cd' : '#d8dadc', position: 'relative', flexShrink: 0,
        transition: 'background 0.2s',
      }}
    >
      <div style={{
        width: 14, height: 14, borderRadius: '50%', background: '#fff',
        position: 'absolute', top: 3, left: on ? 19 : 3, transition: 'left 0.2s',
        boxShadow: '0 1px 3px rgba(25,28,30,0.2)',
      }} />
    </button>
  )
}
